import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { mockStores } from '../utils/mockData';
import { calculateStoreStatus, getStatusColor } from '../utils/storeStatus';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Edit, Copy, Eye, X } from 'lucide-react';
export function PopUpStores() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Get filters from URL params
  const statusParam = searchParams.get('status') || 'all';
  const rangeParam = searchParams.get('range');
  const [filterStatus, setFilterStatus] = useState(statusParam);
  const [searchQuery, setSearchQuery] = useState('');
  // Update filter when URL params change
  useEffect(() => {
    setFilterStatus(statusParam);
  }, [statusParam]);
  const stores = mockStores.map(store => ({
    ...store,
    computedStatus: calculateStoreStatus(store)
  }));
  const filteredStores = stores.filter(store => {
    // Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'closing-soon') {
        const isClosingSoon = store.computedStatus === 'closing-soon' || store.computedStatus === 'open' && new Date(store.settings.closeDate).getTime() - Date.now() < 72 * 3600000;
        if (!isClosingSoon) return false;
      } else if (store.computedStatus !== filterStatus) {
        return false;
      }
    }
    // Date range filter (30d)
    if (rangeParam === '30d' && filterStatus === 'closed') {
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const closeDate = new Date(store.settings.closeDate).getTime();
      if (closeDate < thirtyDaysAgo) return false;
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return store.name.toLowerCase().includes(query) || store.organizationName.toLowerCase().includes(query);
    }
    return true;
  });
  const handleStatusChange = (status: string) => {
    setFilterStatus(status);
    if (status === 'all') {
      searchParams.delete('status');
      searchParams.delete('range');
    } else {
      searchParams.set('status', status);
    }
    setSearchParams(searchParams);
  };
  const handleClearFilters = () => {
    setFilterStatus('all');
    setSearchQuery('');
    setSearchParams({});
  };
  const hasActiveFilters = filterStatus !== 'all' || searchQuery !== '' || rangeParam;
  return <div className="space-y-6" data-id="element-1136">
      <div className="flex justify-between items-center" data-id="element-1137">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1138">Pop-Up Stores</h1>
        <Link to="/pop-up-stores/new" data-id="element-1139">
          <Button data-id="element-1140">Create Pop-Up Store</Button>
        </Link>
      </div>

      <Card data-id="element-1141">
        <div className="flex gap-4 mb-6" data-id="element-1142">
          <div className="flex-1" data-id="element-1143">
            <Input placeholder="Search stores..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1144" />
          </div>
          <div className="w-48" data-id="element-1145">
            <Select options={[{
            value: 'all',
            label: 'All Statuses'
          }, {
            value: 'draft',
            label: 'Draft'
          }, {
            value: 'scheduled',
            label: 'Scheduled'
          }, {
            value: 'open',
            label: 'Open'
          }, {
            value: 'closing-soon',
            label: 'Closing Soon'
          }, {
            value: 'closed',
            label: 'Closed'
          }]} value={filterStatus} onChange={e => handleStatusChange(e.target.value)} data-id="element-1146" />
          </div>
          {hasActiveFilters && <Button variant="outline" onClick={handleClearFilters} data-id="element-1147">
              <X className="h-4 w-4 mr-2" data-id="element-1148" />
              Clear Filters
            </Button>}
        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-200" data-id="element-1149">
            {filterStatus !== 'all' && <Badge variant="blue" data-id="element-1150">
                Status: {filterStatus.replace('-', ' ')}
              </Badge>}
            {rangeParam === '30d' && <Badge variant="blue" data-id="element-1151">Last 30 days</Badge>}
            {searchQuery && <Badge variant="blue" data-id="element-1152">Search: "{searchQuery}"</Badge>}
          </div>}

        <Table data={filteredStores} keyField="id" onRowClick={store => navigate(`/pop-up-stores/${store.id}`)} columns={[{
        header: 'Store Name',
        accessor: s => <div data-id="element-1154">
                  <div className="font-medium text-gray-900" data-id="element-1155">{s.name}</div>
                  <div className="text-xs text-gray-500" data-id="element-1156">
                    {s.organizationName}
                  </div>
                </div>
      }, {
        header: 'Status',
        accessor: s => <Badge variant={getStatusColor(s.computedStatus)} data-id="element-1157">
                  {s.computedStatus.toUpperCase()}
                </Badge>
      }, {
        header: 'Open Date',
        accessor: s => new Date(s.settings.openDate).toLocaleDateString()
      }, {
        header: 'Close Date',
        accessor: s => new Date(s.settings.closeDate).toLocaleDateString()
      }, {
        header: 'Orders',
        accessor: s => s.stats.ordersCount
      }, {
        header: 'Revenue',
        accessor: s => `$${s.stats.revenue.toFixed(2)}`
      }, {
        header: 'Actions',
        accessor: s => <div className="flex space-x-2" onClick={e => e.stopPropagation()} data-id="element-1158">
                  <button className="text-gray-400 hover:text-gray-600" title="Preview" onClick={() => window.open(`/store/${s.slug}?preview=1`, '_blank')} data-id="element-1159">
                    <Eye className="h-4 w-4" data-id="element-1160" />
                  </button>
                  <button className="text-gray-400 hover:text-gray-600" title="Edit" onClick={() => navigate(`/pop-up-stores/${s.id}`)} data-id="element-1161">
                    <Edit className="h-4 w-4" data-id="element-1162" />
                  </button>
                </div>
      }]} data-id="element-1153" />
      </Card>
    </div>;
}